import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, AlertCircle } from 'lucide-react';

const RefundPolicy = () => {
  return (
    <div className="min-h-screen bg-white" data-testid="refund-policy-page">
      {/* Hero Section */}
      <section className="pt-32 pb-12 md:pt-40 md:pb-16 bg-[#F1F5F8]">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-[#163A59] font-medium mb-6 hover:gap-3 transition-all"
            >
              <ArrowLeft size={18} />
              Back to Home
            </Link>
            <h1 className="font-['Source_Serif_4'] text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              Refund Policy
            </h1>
            <p className="text-slate-600">Last updated: February 2026</p>
          </motion.div>
        </div>
      </section>

      {/* Important Notice */}
      <section className="py-8 bg-[#C82032]">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="flex items-start gap-4 text-white">
            <AlertCircle className="w-6 h-6 flex-shrink-0 mt-1" />
            <div>
              <p className="font-semibold text-lg mb-2">Important Notice: No Refunds</p>
              <p className="text-white/95">
                The Geneva Institute of Humanitarian & Development Studies (GIHDS) does not offer refunds under any circumstances. Please read this policy carefully before making any payment.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-12 md:py-20">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="prose prose-slate max-w-none">
            <h2 className="font-['Source_Serif_4'] text-2xl font-semibold text-slate-900 mb-4">1. No-Refund Policy</h2>
            <p className="text-slate-600 leading-relaxed mb-6">
              The Geneva Institute of Humanitarian & Development Studies (GIHDS) maintains a strict no-refund policy. Once payment has been made for any of our programs, <strong>no refunds will be issued under any circumstances</strong>. This policy applies to all programs offered by GIHDS, including:
            </p>
            <ul className="list-disc pl-6 text-slate-600 mb-6 space-y-2">
              <li><strong>Master of Humanitarian Action & Development</strong> — full program fee USD 2,000</li>
              <li><strong>Master of Development Project & Program Management</strong> — full program fee USD 3,500</li>
              <li><strong>Doctor of Humanitarian Action & Development</strong> — full program fee USD 10,800 (USD 3,600 per year)</li>
              <li><strong>AI in Humanitarian Action & Development</strong> (Short Course) — USD 99 (Standard) or USD 59 (Current GIHDS students)</li>
            </ul>
            <p className="text-slate-600 leading-relaxed mb-6">
              Eligible learners from Africa and Asia may apply for the GIHDS 90% scholarship on degree programs; the resulting reduced amount payable is likewise non-refundable once paid.
            </p>

            <h2 className="font-['Source_Serif_4'] text-2xl font-semibold text-slate-900 mb-4">2. Reasons for This Policy</h2>
            <p className="text-slate-600 leading-relaxed mb-4">This policy exists because:</p>
            <ul className="list-disc pl-6 text-slate-600 mb-6 space-y-2">
              <li>GIHDS allocates significant resources at the point of enrollment, including LMS access, course materials, dedicated faculty support, cohort administration, and internal grading capacity</li>
              <li>Scholarship funds are specifically committed to learners who intend to complete their program</li>
              <li>Our operational model, cohort planning, and academic delivery depend on predictable enrollment</li>
              <li>The policy protects the integrity of the admissions process and ensures that only serious and committed candidates enroll</li>
            </ul>

            <h2 className="font-['Source_Serif_4'] text-2xl font-semibold text-slate-900 mb-4">3. Before You Pay</h2>
            <p className="text-slate-600 leading-relaxed mb-4">Before making any payment, please confirm that:</p>
            <ul className="list-disc pl-6 text-slate-600 mb-6 space-y-2">
              <li>You are fully committed to completing your chosen program (4 weeks for the AI in Humanitarian Action & Development short course, 9 months for the Master of Development Project & Program Management, 1 year for the Master of Humanitarian Action & Development, or 3 years for the Doctor of Humanitarian Action & Development)</li>
              <li>You have reviewed the program page in full, including curriculum, requirements, delivery mode, and academic documents awarded upon successful completion</li>
              <li>You have the time and resources to dedicate to online, self-paced study</li>
              <li>You have reliable internet access for the full duration of the program</li>
              <li>You understand that payment is final and non-refundable</li>
            </ul>

            <h2 className="font-['Source_Serif_4'] text-2xl font-semibold text-slate-900 mb-4">4. Situations Where No Refund Will Be Issued</h2>
            <p className="text-slate-600 leading-relaxed mb-4">Refunds will not be issued in any situation, including but not limited to:</p>
            <ul className="list-disc pl-6 text-slate-600 mb-6 space-y-2">
              <li>Change of mind, personal circumstances, or shifting priorities</li>
              <li>Inability or unwillingness to continue with the program</li>
              <li>Dissatisfaction with the course content, delivery, or platform</li>
              <li>Technical issues on the learner's side (internet, device, or software problems)</li>
              <li>Failure to meet program requirements or academic standards</li>
              <li>Withdrawal from or dismissal from a program for reasons of academic misconduct or breach of these terms</li>
              <li>Any other reason whatsoever</li>
            </ul>

            <h2 className="font-['Source_Serif_4'] text-2xl font-semibold text-slate-900 mb-4">5. Deferral</h2>
            <p className="text-slate-600 leading-relaxed mb-6">
              In exceptional circumstances—such as a serious documented medical emergency—a learner may request a one-time deferral to a future cohort. Deferrals are considered on a case-by-case basis, are subject to written approval by GIHDS administration, and are not guaranteed. A granted deferral does not constitute a refund and does not entitle the learner to any monetary reimbursement.
            </p>

            <h2 className="font-['Source_Serif_4'] text-2xl font-semibold text-slate-900 mb-4">6. Chargebacks & Disputes</h2>
            <p className="text-slate-600 leading-relaxed mb-6">
              Because this no-refund policy is disclosed prior to payment and forms part of our <Link to="/terms-of-service" className="text-[#163A59] underline">Terms of Use</Link>, initiating a chargeback or payment dispute contrary to this policy may result in immediate suspension of access, removal from the enrolled program, and legal recovery of costs incurred by GIHDS.
            </p>

            <h2 className="font-['Source_Serif_4'] text-2xl font-semibold text-slate-900 mb-4">7. Acknowledgment</h2>
            <p className="text-slate-600 leading-relaxed mb-6">
              By submitting your application and making payment, you acknowledge that you have read, understood, and agreed to this No-Refund Policy. You confirm that you are making an informed decision to enroll and accept full responsibility for that decision.
            </p>

            <h2 className="font-['Source_Serif_4'] text-2xl font-semibold text-slate-900 mb-4">8. Contact</h2>
            <p className="text-slate-600 leading-relaxed mb-6">
              If you have any questions about this policy <em>before</em> making payment, please contact us:
            </p>
            <div className="bg-[#F1F5F8] p-6 rounded-sm">
              <p className="text-slate-900 font-semibold">Geneva Institute of Humanitarian & Development Studies (GIHDS)</p>
              <p className="text-slate-600">Email: admissions@gihds.org</p>
              <p className="text-slate-600">Website: www.gihds.org</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default RefundPolicy;
